import { X } from "lucide-react";

import { Alert, AlertDescription, Button } from "@lagoon/ui";

/**
 * A reason code that reached a signed-in reader (LGN-126).
 *
 * The `reauth_*` codes are redirects the platform makes back into the shell,
 * not into the sign-in screen: the person is still signed in, their
 * confirmation simply did not complete. `App` never read `?error=`, so all
 * three arrived, sat in the address bar and said nothing at all, and the action
 * they belonged to looked as though it had silently done nothing.
 *
 * Dismissible because it is about something that has already happened rather
 * than about the state of the screen underneath it, and because it is drawn
 * above every route: nothing else would ever take it away. Dismissing also
 * strips the parameter from the URL, so a refresh does not resurrect it.
 *
 * `Alert` is a live region, which is what makes this reach somebody who has
 * just been returned from Auth0 and is looking at the button they pressed
 * rather than at the top of the page.
 */
export function AuthErrorBanner(props: {
  message: string;
  onDismiss: () => void;
}): React.ReactElement {
  return (
    <Alert tone="danger">
      <AlertDescription className="lg:flex lg:items-start lg:justify-between lg:gap-3">
        <span>{props.message}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="lg:-my-1 lg:shrink-0"
          onClick={props.onDismiss}
        >
          <X />
          <span className="lg:sr-only">Dismiss</span>
        </Button>
      </AlertDescription>
    </Alert>
  );
}
